import { mdiArrowCollapse, mdiArrowExpand, mdiTranslate } from '@mdi/js'
import Icon from '@mdi/react'
import { PrimitiveAtom, useAtom } from 'jotai'
import { ChangeEventHandler, KeyboardEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import StopCircle from '@/components/assets/StopCircle'
import CSI from '@/components/CSI/Container'
import { Box } from '@/components/ui/box'
import { Button } from '@/components/ui/button'
import { Flex } from '@/components/ui/flex'
import { VStack } from '@/components/ui/stack'
import useColorModeValue from '@/hooks/useColorModeValue'
import Settings from '@/pages/components/gigiChat/settings'
import IconSendDark from '@/static/img/Icon_Send_dark.svg?react'
import IconSendLight from '@/static/img/Icon_Send_light.svg?react'
import { chatSettingsAtom, isServerRespondingAtom, partialMessagesAtom } from '@/store/gigiChat/ChatAtom'
import { selectedLanguagesAtom, setQuestionActionAtom } from '@/store/translate'
import { ISearchProps, IShortMessageObject } from '@/types/gigiChat/Chat'
import { IConversation } from '@/types/gigiChat/Conversation'
import { IConversationSettings } from '@/types/gigiChat/ConversationSettings'
import { stringFromBase64URL } from '@/utils/base64.ts'
import { getAndRemovePropertyFromUrlOnce } from '@/utils/chat.ts'

import AutoResizeInput from './AutoResizeInput'
import CSISearchTooltip from './tooltip'

const Search = ({ handleSendMessage, onFullInputExpand, isInputFullExpanded, onStopStreaming, onCreateNewChat }: ISearchProps) => {
    const { t } = useTranslation()
    const [currentMessage, setCurrentMessage] = useState<string>('')
    const [shouldDisplayExpand, setShouldDisplayExpand] = useState<boolean>(false)
    const [isServerResponding] = useAtom<PrimitiveAtom<boolean>>(isServerRespondingAtom)
    const [chatSettings] = useAtom<PrimitiveAtom<IConversationSettings | null>>(chatSettingsAtom)
    const [partialMessage] = useAtom<PrimitiveAtom<Array<IShortMessageObject> | Array<IConversation>>>(partialMessagesAtom)
    const [selectedLanguages] = useAtom(selectedLanguagesAtom)
    const [isSend, setISend] = useState<boolean>(false)
    const ButtonIcon = useColorModeValue(IconSendLight, IconSendDark)
    const iconColor = useColorModeValue('var(--chakra-colors-white)', 'var(--chakra-colors-dark-700)')
    const sendIcon =
        chatSettings?.processingStrategy === 'Translation' ? <Icon path={mdiTranslate} size="24px" color={iconColor} /> : <ButtonIcon />

    useEffect(() => {
        const urlPrompt = getAndRemovePropertyFromUrlOnce('prompt')
        const encoding = getAndRemovePropertyFromUrlOnce('encoding')
        if (urlPrompt) {
            if (encoding?.toLowerCase() === 'base64url') {
                try {
                    const decoded = stringFromBase64URL(urlPrompt)
                    setCurrentMessage(decoded)
                } catch (_e) {
                    console.warn('Failed to base64URL decode', urlPrompt)
                    console.info('Test your input in console with stringFromBase64URL and stringToBase64URL functions')
                    setCurrentMessage(urlPrompt)
                }
            } else {
                setCurrentMessage(urlPrompt)
            }
        }
    }, [])

    useEffect(() => {
        if (
            partialMessage[partialMessage.length - 1] &&
            !partialMessage[partialMessage.length - 1].answer &&
            currentMessage &&
            isSend &&
            chatSettings &&
            chatSettings?.processingStrategy === 'Translation'
        ) {
            handleSendMessage(currentMessage)
            setCurrentMessage('')
            if (isInputFullExpanded) {
                onFullInputExpand()
            }
            setShouldDisplayExpand(false)
            setISend(false)
        }
    }, [partialMessage, currentMessage, handleSendMessage, isInputFullExpanded, onFullInputExpand, isSend, chatSettings])

    const onSend = () => {
        const send = async () => {
            if (!isServerResponding) {
                if (chatSettings && chatSettings?.processingStrategy === 'Translation') {
                    setQuestionActionAtom('QTextTranslate')
                }

                // NOTE: review the create new chat on thanslation page once translation feature is deployed in prod
                if (
                    onCreateNewChat &&
                    partialMessage[partialMessage.length - 1].question &&
                    chatSettings &&
                    chatSettings?.processingStrategy === 'Translation'
                ) {
                    await onCreateNewChat()
                    setISend(true)
                } else {
                    handleSendMessage(currentMessage)
                    setCurrentMessage('')
                    if (isInputFullExpanded) {
                        onFullInputExpand()
                    }
                    setShouldDisplayExpand(false)
                }
            }
        }
        void send()
    }

    const openCloseFullExpand = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        const currentInputHeight: number = e.currentTarget.offsetHeight
        if (currentInputHeight >= 90) {
            setShouldDisplayExpand(true)
        } else if (shouldDisplayExpand) {
            setShouldDisplayExpand(false)
        }
    }

    const onKeydown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && e.altKey) {
            e.preventDefault()
            const textarea = e.target as HTMLTextAreaElement
            const start = textarea.selectionStart
            const end = textarea.selectionEnd
            const beforeCursor = currentMessage.substring(0, start)
            const afterCursor = currentMessage.substring(end, currentMessage.length)

            setCurrentMessage(beforeCursor + '\n' + afterCursor)

            // Set the cursor position after inserting the newline
            setTimeout(() => {
                textarea.selectionStart = textarea.selectionEnd = start + 1
            }, 0)
        }
        if (e.key === 'Enter' && !e.shiftKey && !e.altKey) {
            e.preventDefault()
            if (currentMessage.trim() !== '') {
                onSend()
            }
        } else {
            openCloseFullExpand(e)
        }
    }

    const onKeyUp = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        openCloseFullExpand(e)
    }

    const onInput: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
        setCurrentMessage(e.target.value)
    }

    return (
        <Box
            mt="auto"
            pb="24px"
            display={isInputFullExpanded ? 'flex' : 'block'}
            flexDir="column"
            height={isInputFullExpanded ? '100%' : 'auto'}
        >
            <Box flexGrow={0} my="8px">
                <Settings />
            </Box>

            <Flex flexGrow={isInputFullExpanded ? 1 : 0} w="100%">
                <AutoResizeInput
                    placeholder={t('chat.ask.inputPlaceholder')}
                    value={currentMessage}
                    onKeyDown={onKeydown}
                    onKeyUp={onKeyUp}
                    onInput={onInput}
                    isInputFullExpanded={isInputFullExpanded}
                    alignSelf="flex-end"
                    display="block"
                    handleMessage={handleSendMessage}
                />
                <VStack
                    pl="8px"
                    align="left"
                    justifyContent="space-between"
                    alignItems="center"
                    alignSelf={shouldDisplayExpand ? 'stretch' : 'end'}
                >
                    {shouldDisplayExpand && (
                        <Box onClick={onFullInputExpand} cursor="pointer" paddingTop="16px">
                            {isInputFullExpanded ? <Icon path={mdiArrowCollapse} size={0.8} /> : <Icon path={mdiArrowExpand} size={0.8} />}
                        </Box>
                    )}

                    {!isServerResponding || currentMessage.trim() !== '' ? (
                        <Button
                            data-testid="chat-send-button"
                            variant="send"
                            aria-label="Send"
                            onClick={onSend}
                            disabled={
                                isServerResponding ||
                                currentMessage.trim() === '' ||
                                (chatSettings?.processingStrategy === 'Translation' && selectedLanguages.length === 0)
                            }
                        >
                            {sendIcon}
                        </Button>
                    ) : (
                        <Button data-testid="chat-stop-button" variant="send" aria-label="Send" onClick={onStopStreaming}>
                            <StopCircle color="var(--chakra-colors-white)" />
                        </Button>
                    )}
                </VStack>
            </Flex>

            <Flex flexGrow={0} mt="12px" zIndex="2" gap={2} minHeight="18px">
                {chatSettings && (
                    <>
                        <CSI isSearch />
                        <CSISearchTooltip chatSettings={chatSettings} isSearch />
                    </>
                )}
            </Flex>
        </Box>
    )
}

export default Search
